import { useEffect, useRef, useState, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ArrowDown, Upload, Cpu, Gauge, Handshake,
  FileText, Table2, BookOpenCheck, ScrollText, Landmark, ShieldCheck,
  Receipt, PieChart, Percent, Scale, BadgeCheck,
  Building2, UserRound,
} from "lucide-react";
import { PriceTable } from "../components/landing/PriceTable";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { FAQTeaser } from "../components/landing/FAQTeaser";

const JOURNEY = [
  { icon: Building2, title: "Get your report & tell us about your business", body: "Get your Capital Readiness Report, then complete your business profile so we understand who you are and where you are going." },
  { icon: Upload, title: "Upload your documents", body: "Upload your financial and business documents. Do not have everything yet? Our team can help you build what you need." },
  { icon: Cpu, title: "We score your business", body: "We review your documents and score your business the way real funders do — so you know exactly where you stand." },
  { icon: Gauge, title: "Your Capital Readiness Score & Report", body: "See your Capital Readiness Score and download your Capital Readiness Report — which funders fit, how much you could raise, and exactly what to fix." },
  { icon: Handshake, title: "Clinq goes to work", body: "If your score is strong, Clinq connects you directly to the right funders in our network. If not, your Relationship Manager walks with you until you are there." },
];

const NEXT_CARDS = [
  { icon: UserRound, title: "Your Personal Relationship Manager",
    body: "From the moment you get your Capital Readiness Report at Clinq, a dedicated Relationship Manager is assigned to your business. They monitor your Capital Readiness journey, check in proactively, connect you to the right resources at the right time, and make sure nothing falls through the cracks. When your score is ready, they are the ones who open the door to funders.",
    cta: "Meet your Relationship Manager", to: "/your-rm" },
];

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.2, rootMargin: "0px 0px -40px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{
        transition: "opacity 0.7s ease, transform 0.7s ease",
        transitionDelay: `${delay}ms`,
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(24px)",
      }}>
      {children}
    </div>
  );
}

const CHECKLIST = [
  { icon: FileText, title: "Pitch deck", why: "The story of your business — team, product, market and the ask." },
  { icon: Table2, title: "Financial model", why: "3–5 year projections funders can interrogate line by line." },
  { icon: BookOpenCheck, title: "Management accounts", why: "Proof your revenue is real. Clean, recent numbers build trust fast." },
  { icon: ScrollText, title: "Legal documents", why: "Registration, articles and resolutions — you exist on paper." },
  { icon: Landmark, title: "Bank statements", why: "6+ months of statements unlock revenue-based finance options." },
  { icon: ShieldCheck, title: "Audited accounts", why: "Required by banks and DFIs — independently checked numbers." },
  { icon: Receipt, title: "Tax compliance", why: "A current TCC shows large funders you're in good standing." },
  { icon: PieChart, title: "Cap table", why: "Who owns what — investors check this before buying in." },
];

const TIERS = [
  { band: "Pre-revenue", price: "KES 4,500", note: "Not yet generating income" },
  { band: "Post-revenue", price: "KES 9,500", note: "Any monthly revenue level", popular: true },
];

const TIER_FEATURES = [
  { label: "Full Capital Readiness Report (web + PDF)", href: "/sample-report", hrefLabel: "View sample" },
  { label: "3 re-runs included (4 credits total)",
    info: "Unlocking your Capital Readiness Report gives you 4 analysis credits. The first is used when we generate your report; the remaining 3 let you update your documents and re-run your analysis to see your Capital Readiness Score improve — no extra charge." },
  "Funder match list for your mandate fit",
  "Borrowing-capacity estimate",
  "Improvement roadmap, step by step",
];

export default function ForBusiness() {
  const nav = useNavigate();
  return (
    <div data-testid="for-business-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      {/* Header */}
      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <Link to="/" data-testid="for-business-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#7BD3CE]">
            Clinq for Business · Unlock Your Capital Potential
          </p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Everything funders will ask for — <span className="text-emerald-300">ready before they ask.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/75 sm:text-lg">
            Clinq turns your documents into a Capital Readiness Score, a funder-grade
            report and warm introductions to the capital that fits your business.
          </p>
          <button
            data-testid="for-business-header-cta"
            onClick={() => nav("/get-started")}
            className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667] sm:w-auto"
          >
            Start free — get my business score
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </header>

      {/* How Clinq Works — founder journey */}
      <section data-testid="for-business-journey" className="border-b border-neutral-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">How Clinq Works</p>
          <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
            Your journey from first login to funder-ready — in five clear steps.
          </h2>

          {/* Journey diagram — connected step cards, desktop horizontal / mobile vertical */}
          <div className="mt-14 flex flex-col items-stretch gap-4 lg:flex-row lg:gap-2">
            {JOURNEY.map((s, i) => {
              const Icon = s.icon;
              const last = i === JOURNEY.length - 1;
              return (
                <Fragment key={s.title}>
                  <Reveal delay={i * 130} className="flex-1">
                    <div data-testid={`for-business-journey-step-${i + 1}`}
                      className={`relative h-full rounded-2xl border bg-white p-6 pt-9 transition-all hover:-translate-y-1 hover:shadow-xl ${
                        last ? "border-[#ECC764]/60 bg-gradient-to-b from-[#ECC764]/10 to-white hover:border-[#ECC764]"
                             : "border-neutral-200 hover:border-[#0A8B85]/40"}`}>
                      <span className={`absolute -top-4 left-6 flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold text-white ring-4 ring-white ${
                        last ? "bg-[#D1A330]" : "bg-[#04302E]"}`}>
                        {i + 1}
                      </span>
                      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                        last ? "bg-[#ECC764]/25 text-[#8a6a1c]" : "bg-[#0A8B85]/10 text-[#0A8B85]"}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mt-3 text-base font-bold text-neutral-900">{s.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{s.body}</p>
                    </div>
                  </Reveal>
                  {!last && (
                    <div className="flex shrink-0 items-center justify-center" aria-hidden="true">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#0A8B85]/25 bg-[#0A8B85]/10 text-[#0A8B85]">
                        <ArrowRight className="hidden h-4 w-4 lg:block" />
                        <ArrowDown className="h-4 w-4 lg:hidden" />
                      </span>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>

          {/* What happens next */}
          <div data-testid="for-business-what-next" className="mt-16 rounded-3xl bg-[#F4F8F8] p-8 sm:p-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D1A330]">We Are With You Every Step</p>
            <h3 className="mt-3 text-xl font-extrabold tracking-tight text-neutral-900 sm:text-2xl">
              You don't have to figure this out alone.
            </h3>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-neutral-600 md:text-lg" data-testid="for-business-what-next-sub">
              Every founder on Clinq is assigned a dedicated Relationship Manager — a real person who knows your
              business, tracks your progress, and walks with you every step of the way to funding.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5">
              {NEXT_CARDS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.title} delay={i * 130} className="h-full">
                    <div data-testid={`for-business-next-card-${i + 1}`}
                      className="flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-[#0A8B85]/40 hover:shadow-lg">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0A8B85]/10 text-[#0A8B85]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="mt-4 text-base font-bold text-neutral-900">{c.title}</h4>
                    <p className="mt-1.5 flex-1 text-sm leading-relaxed text-neutral-600">{c.body}</p>
                    <Link to={c.to} data-testid={`for-business-next-cta-${i + 1}`}
                      className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#0A8B85] transition-colors hover:text-[#066667]">
                      {c.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How funders decide — emphasized */}
      <section data-testid="for-business-funder-decisions" className="border-b border-neutral-200 bg-[#F4F8F8]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">
            Why documents decide everything
          </p>
          <div className="mt-4 grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-16">
            <div>
              <h2 className="text-2xl font-extrabold leading-snug tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl">
                Funders don&rsquo;t fund promises.<br />
                <span className="text-[#0A8B85]">They fund what your documents prove.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">
                Before a funder ever meets you, the decision is already taking shape — on
                paper. Investment committees, credit teams and angels all follow the same
                process: they screen your <strong>pitch deck</strong> to understand the
                opportunity, interrogate your <strong>financial model</strong> to test your
                assumptions, and verify your <strong>management accounts</strong> to confirm
                the numbers are real.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">
                A complete, consistent document pack signals a business that&rsquo;s ready for
                capital. Gaps or contradictions send funders to the next deal — usually
                without telling you why. That&rsquo;s the problem Clinq solves: we get your
                documentation to the standard funders decide on.
              </p>
            </div>
            <div className="space-y-4">
              {[
                ["Screen", "Your pitch deck earns (or loses) the first meeting — team, market, traction, ask."],
                ["Interrogate", "Your financial model shows whether the returns justify the risk."],
                ["Verify", "Management accounts and bank statements prove the revenue is real."],
                ["Decide", "Consistent, complete documents move you from the 'maybe' pile to a term sheet."],
              ].map(([t, b], i) => (
                <div key={t} className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A8B85] text-xs font-extrabold text-white">
                    0{i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-neutral-900">{t}</p>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-600">{b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video */}
      <section data-testid="for-business-video-section" className="bg-[#F4F8F8]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 py-20 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Watch it in action</p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
              Meet Amina — from &ldquo;not ready&rdquo; to funder-ready.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              In three minutes, see how a Kenyan founder goes from scattered documents to
              a Capital Readiness Score and real funder conversations with Clinq.
            </p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-[0_30px_60px_-20px_rgba(10,139,133,0.3)]">
            <video
              data-testid="for-business-video"
              className="aspect-[16/9] w-full bg-black object-cover"
              src="/videos/clinq-founder-explainer.mp4"
              poster="/videos/explainer-poster.jpg"
              controls
              playsInline
              preload="metadata"
            />
          </div>
        </div>
      </section>

      {/* Investor Document Checklist */}
      <section data-testid="for-business-checklist" className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Investor Document Checklist</p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
              The documents that open doors.
            </h2>
          </div>
          <Link to="/why-documents-matter" data-testid="for-business-docs-link"
            className="text-sm font-semibold text-[#0A8B85] underline-offset-4 hover:underline">
            Why each document matters →
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CHECKLIST.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.title} className="rounded-2xl border border-neutral-200 bg-white p-6 transition-colors hover:border-[#0A8B85]/40 hover:bg-[#F4F8F8]">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold">{d.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{d.why}</p>
              </div>
            );
          })}
        </div>
        <div data-testid="for-business-docs-help-note"
          className="mt-10 flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#0A8B85]/25 bg-[#F4F8F8] p-6 sm:flex-row sm:items-center">
          <p className="text-base text-neutral-700">
            Don&rsquo;t have all these documents?{" "}
            <span className="font-bold text-[#0A8B85]">Clinq can help build them for you.</span>
          </p>
          <button
            data-testid="for-business-docs-help-cta"
            onClick={() => nav("/get-started")}
            className="group inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-[#066667] sm:w-auto"
          >
            Start building mine
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </section>

      {/* High-score capital raising partnership */}
      <section data-testid="for-business-raise-with-you" className="bg-[#F4F8F8]">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D1A330]">
            For high-scoring businesses
          </p>
          <div className="mt-4 grid grid-cols-1 gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
            <div>
              <h2 className="text-2xl font-extrabold leading-snug tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl">
                Score high, and Clinq raises <span className="text-[#0A8B85]">with you.</span>
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">
                When your Capital Readiness Score is strong, we don&rsquo;t just hand you a
                list of names. <strong>Clinq reaches out to funders on your behalf</strong> and
                walks with you through the full capital raising journey — from the first
                introduction, through negotiations and due diligence, all the way to final
                close and drawdown of funds.
              </p>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-700 sm:text-lg">
                Our incentives sit on your side of the table: Clinq earns a success fee of{" "}
                <strong>5% or under of the capital you actually draw down</strong>. No
                drawdown, no fee — we only win when you get funded.
              </p>
              <div data-testid="for-business-success-fee-chip"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#D1A330]/40 bg-[#ECC764]/15 px-4 py-2 text-sm font-bold text-[#8a6a1c]">
                <Percent className="h-4 w-4" /> Success fee of 5% or under — paid only on funds drawn down
              </div>
            </div>
            <div className="space-y-4">
              {[
                [Handshake, "Introductions", "We approach mandate-matched funders for you, with your score and report leading the conversation."],
                [Scale, "Negotiations", "We stay in the room — helping you navigate term sheets, diligence requests and structuring."],
                [BadgeCheck, "Close & drawdown", "We see the deal through to signed agreements and funds landing in your account."],
              ].map(([Icon, t, b], i) => (
                <div key={t} data-testid={`for-business-raise-step-${i + 1}`}
                  className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0A8B85]/10 text-[#0A8B85]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-neutral-900">{i + 1}. {t}</p>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-600">{b}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="for-business-pricing" className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#ECC764]">Pricing</p>
          <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
            Your quiz score is free. Your full report is one simple payment.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            Pre-revenue businesses pay KES 4,500; post-revenue businesses pay KES 9,500.
            One-time payment — no subscription, no surprises. Expert review and funder
            connection are included once your score reaches 55+.
          </p>
          <PriceTable basisLabel="Your business stage" rows={TIERS} features={TIER_FEATURES} testidPrefix="for-business-pricing" />
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <button
              data-testid="for-business-pricing-cta"
              onClick={() => nav("/get-started")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-[#066667] sm:w-auto"
            >
              Get started free
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="text-sm text-white/50">Secure payments via Paystack · one payment unlocks your full 5-step journey</p>
          </div>
        </div>
      </section>

      <FAQTeaser />

      <Footer />
    </div>
  );
}
